import {defineMessages, MessageDescriptor} from 'react-intl'

/**
 * Catálogo de Automatizaciones (`/automations`). Namespace raíz de la Fase 1.9:
 * `comms.*` — mensajería (chat, campañas, plantillas, destinatarios,
 * automatizaciones).
 *
 * Lo consumen `AutomationsPage`, `AutomationWizard` y `AutomationLogModal`, más
 * los starters de `constants/automationStarters.ts` (que guardan el EN canónico
 * para persistirlo y el descriptor para mostrarlo).
 *
 * No se traducen: nombres de canal (Email, SMS), los valores de enum servidos por
 * el backend (`log.channel`, `log.status`, `template.category`) ni el contenido
 * de las plantillas sugeridas (viaja al backend como dato).
 */
export const AUTOMATION_MESSAGES = defineMessages({
  // ── Página ──
  automations: {id: 'MENU.item.automations', defaultMessage: 'Automations'},
  subtitle: {
    id: 'comms.automations.page.subtitle',
    defaultMessage: 'Automated Email and SMS messages based on policy and payment events.',
  },
  newAutomation: {id: 'comms.automations.page.new', defaultMessage: 'New Automation'},
  searchPlaceholder: {
    id: 'comms.automations.page.searchPlaceholder',
    defaultMessage: 'Search automation...',
  },
  allChannels: {id: 'comms.field.allChannels', defaultMessage: 'All channels'},
  errorLoading: {
    id: 'comms.automations.page.errorLoading',
    defaultMessage: 'Error loading automations: {message}',
  },
  emptyTitle: {id: 'comms.automations.page.emptyTitle', defaultMessage: 'No automations yet'},
  emptyHint: {
    id: 'comms.automations.page.emptyHint',
    defaultMessage: 'Create your first automation to start sending messages automatically.',
  },
  emptyAction: {
    id: 'comms.automations.page.emptyAction',
    defaultMessage: 'Create first automation',
  },

  // ── Tabla ──
  name: {id: 'common.field.name', defaultMessage: 'Name'},
  scope: {id: 'common.field.scope', defaultMessage: 'Scope'},
  trigger: {id: 'comms.automations.field.trigger', defaultMessage: 'Trigger'},
  channel: {id: 'comms.field.channel', defaultMessage: 'Channel'},
  template: {id: 'comms.field.template', defaultMessage: 'Template'},
  sent: {id: 'comms.field.sent', defaultMessage: 'Sent'},
  lastSent: {id: 'comms.automations.field.lastSent', defaultMessage: 'Last sent'},
  actions: {id: 'common.field.actions', defaultMessage: 'Actions'},
  switchActive: {
    id: 'comms.automations.table.switchActive',
    defaultMessage: 'Active — click to disable',
  },
  switchInactive: {
    id: 'comms.automations.table.switchInactive',
    defaultMessage: 'Inactive — click to enable',
  },
  scopeOrgShort: {id: 'comms.automations.scope.orgShort', defaultMessage: 'Org'},
  scopePersonal: {id: 'comms.automations.scope.personal', defaultMessage: 'Personal'},
  noTemplate: {id: 'comms.automations.table.noTemplate', defaultMessage: 'No template'},
  viewHistory: {id: 'comms.automations.action.viewHistory', defaultMessage: 'View history'},
  edit: {id: 'common.action.edit', defaultMessage: 'Edit'},
  delete: {id: 'common.action.delete', defaultMessage: 'Delete'},
  cancel: {id: 'common.action.cancel', defaultMessage: 'Cancel'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  preview: {id: 'common.action.preview', defaultMessage: 'Preview'},
  saveChanges: {id: 'common.action.saveChanges', defaultMessage: 'Save changes'},
  loading: {id: 'common.loading', defaultMessage: 'Loading...'},

  // ── Modal de borrado ──
  deleteTitle: {id: 'comms.automations.delete.title', defaultMessage: 'Delete Automation'},
  deleteConfirm: {id: 'common.confirm.areYouSure', defaultMessage: 'Are you sure?'},
  deleteBody: {
    id: 'common.confirm.deleteBody',
    defaultMessage: 'You are about to delete <b>"{name}"</b>. This cannot be undone.',
  },

  // ── Toasts de la página ──
  toastEnabled: {id: 'comms.automations.toast.enabled', defaultMessage: 'Automation enabled'},
  toastDisabled: {id: 'comms.automations.toast.disabled', defaultMessage: 'Automation disabled'},
  toastErrorToggle: {
    id: 'comms.automations.toast.errorToggle',
    defaultMessage: 'Error changing status',
  },
  toastDeleted: {id: 'comms.automations.toast.deleted', defaultMessage: 'Automation deleted'},
  toastErrorDelete: {
    id: 'comms.automations.toast.errorDelete',
    defaultMessage: 'Error deleting automation',
  },

  // ── Disparadores ──
  triggerHappyBirthday: {
    id: 'comms.automations.trigger.happyBirthday',
    defaultMessage: 'Happy Birthday',
  },
  triggerConditionalLeadShort: {
    id: 'comms.automations.trigger.conditionalLeadShort',
    defaultMessage: 'Conditional (Leads)',
  },
  triggerConditionalPolicyShort: {
    id: 'comms.automations.trigger.conditionalPolicyShort',
    defaultMessage: 'Conditional (Policy)',
  },
  triggerConditionalLead: {
    id: 'comms.automations.trigger.conditionalLead',
    defaultMessage: 'Conditional Action (Leads)',
  },
  triggerConditionalPolicy: {
    id: 'comms.automations.trigger.conditionalPolicy',
    defaultMessage: 'Conditional Action (Policy/Quote)',
  },

  // ── Condiciones ──
  conditionLeadDuration: {
    id: 'comms.automations.condition.leadDuration',
    defaultMessage: 'Lead Duration',
  },
  conditionLeadStage: {
    id: 'comms.automations.condition.leadStage',
    defaultMessage: 'Lead Stage (pipeline name)',
  },
  conditionStateChange: {
    id: 'comms.automations.condition.stateChange',
    defaultMessage: 'State Change + duration in that state',
  },
  conditionCrossSellAuto: {
    id: 'comms.automations.condition.crossSellAuto',
    defaultMessage: 'Has Auto without Auto Policy + policy duration',
  },
  conditionCrossSellHome: {
    id: 'comms.automations.condition.crossSellHome',
    defaultMessage: 'Has Home without Home Policy + policy duration',
  },
  conditionPolicyCompleted: {
    id: 'comms.automations.condition.policyCompleted',
    defaultMessage: 'Completed Policy + duration in that state',
  },
  conditionByCarrier: {
    id: 'comms.automations.condition.byCarrier',
    defaultMessage: 'By Carrier (Select Carrier)',
  },

  // ── Modal de historial ──
  logTotalRuns: {id: 'comms.automations.log.totalRuns', defaultMessage: 'Total runs'},
  logWithBirthday: {id: 'comms.automations.log.withBirthday', defaultMessage: 'With birthday'},
  logEligibleToday: {id: 'comms.automations.log.eligibleToday', defaultMessage: 'Eligible today'},
  logTabHistory: {id: 'comms.automations.log.tabHistory', defaultMessage: 'Sending history'},
  logTabToday: {id: 'comms.automations.log.tabToday', defaultMessage: 'Scheduled today'},
  logEmpty: {id: 'comms.automations.log.empty', defaultMessage: 'No sending records yet'},
  logColDate: {id: 'comms.automations.log.colDate', defaultMessage: 'Date'},
  logColError: {id: 'comms.field.error', defaultMessage: 'Error'},
  logTodayEmpty: {
    id: 'comms.automations.log.todayEmpty',
    defaultMessage: 'No contacts scheduled for today',
  },
  logColBirthday: {id: 'comms.automations.log.colBirthday', defaultMessage: 'Birthday'},
  logSentToday: {id: 'comms.automations.log.sentToday', defaultMessage: 'Sent today'},
  contact: {id: 'common.field.contact', defaultMessage: 'Contact'},
  status: {id: 'common.field.status', defaultMessage: 'Status'},
  pending: {id: 'common.status.pending', defaultMessage: 'Pending'},

  // ── Asistente: cabecera y panel de flujo ──
  editAutomation: {
    id: 'comms.automations.wizard.editTitle',
    defaultMessage: 'Edit Automation',
  },
  workflowPreview: {
    id: 'comms.automations.wizard.workflowPreview',
    defaultMessage: 'Workflow Preview',
  },
  day: {id: 'comms.automations.wizard.day', defaultMessage: 'Day {days}'},
  sendEmail: {id: 'comms.automations.wizard.sendEmail', defaultMessage: 'Send Email'},
  sendSms: {id: 'comms.automations.wizard.sendSms', defaultMessage: 'Send SMS'},
  noTemplateSelected: {
    id: 'comms.automations.wizard.noTemplateSelected',
    defaultMessage: 'No template selected',
  },
  endOfWorkflow: {
    id: 'comms.automations.wizard.endOfWorkflow',
    defaultMessage: 'End of workflow',
  },
  summaryName: {
    id: 'comms.automations.wizard.summaryName',
    defaultMessage: '<b>Name:</b> {name}',
  },

  // ── Asistente: paso 0 (biblioteca) ──
  chooseStart: {
    id: 'comms.automations.wizard.chooseStart',
    defaultMessage: 'Choose a starting point',
  },
  startFromScratch: {
    id: 'comms.automations.wizard.startFromScratch',
    defaultMessage: 'Start from scratch',
  },
  searchTemplates: {
    id: 'comms.automations.wizard.searchTemplates',
    defaultMessage: 'Search templates...',
  },
  myTemplates: {id: 'comms.automations.wizard.myTemplates', defaultMessage: 'My Templates'},
  recommendedTemplates: {
    id: 'comms.automations.wizard.recommendedTemplates',
    defaultMessage: 'Recommended Templates',
  },
  noTemplatesMatch: {
    id: 'comms.automations.wizard.noTemplatesMatch',
    defaultMessage: 'No templates match your search.',
  },
  customTemplateMeta: {
    id: 'comms.automations.wizard.customTemplateMeta',
    defaultMessage: '{channel} · {trigger} · Day {days}',
  },
  starterMeta: {
    id: 'comms.automations.wizard.starterMeta',
    defaultMessage: '{channel} · Day {days}',
  },
  use: {id: 'common.action.use', defaultMessage: 'Use'},
  alreadySaved: {
    id: 'comms.automations.wizard.alreadySaved',
    defaultMessage: 'Already in your library',
  },
  saveToLibrary: {
    id: 'comms.automations.wizard.saveToLibrary',
    defaultMessage: 'Save to my templates',
  },
  saveShort: {id: 'comms.automations.wizard.saveShort', defaultMessage: '+ Save'},

  // ── Asistente: pasos ──
  step1: {id: 'comms.automations.wizard.step1', defaultMessage: '1. Trigger'},
  step2: {id: 'comms.automations.wizard.step2', defaultMessage: '2. Channel + Template'},
  step3: {id: 'comms.automations.wizard.step3', defaultMessage: '3. Configuration'},
  step1Title: {
    id: 'comms.automations.wizard.step1Title',
    defaultMessage: 'What event triggers this automation?',
  },
  tabGeneral: {id: 'common.value.general', defaultMessage: 'General'},
  tabLeads: {id: 'MENU.item.leads', defaultMessage: 'Leads'},
  tabPolicy: {id: 'comms.automations.wizard.tabPolicy', defaultMessage: 'Policy / Quote'},

  leadSettings: {
    id: 'comms.automations.wizard.leadSettings',
    defaultMessage: 'Lead Condition Settings',
  },
  policySettings: {
    id: 'comms.automations.wizard.policySettings',
    defaultMessage: 'Policy/Quote Condition Settings',
  },
  conditionRule: {id: 'comms.automations.wizard.conditionRule', defaultMessage: 'Condition Rule'},
  selectRule: {id: 'comms.automations.wizard.selectRule', defaultMessage: 'Select a rule...'},
  durationDays: {id: 'comms.automations.wizard.durationDays', defaultMessage: 'Duration (Days)'},
  durationHint: {
    id: 'comms.automations.wizard.durationHint',
    defaultMessage: 'Contacts whose lead was created exactly this many days ago.',
  },
  stageName: {id: 'comms.automations.wizard.stageName', defaultMessage: 'Stage Name'},
  stagePlaceholder: {
    id: 'comms.automations.wizard.stagePlaceholder',
    defaultMessage: 'e.g. Qualified, In Progress...',
  },
  stageHint: {
    id: 'comms.automations.wizard.stageHint',
    defaultMessage: 'Partial match on your pipeline stage name.',
  },
  daysAgoUpdated: {
    id: 'comms.automations.wizard.daysAgoUpdated',
    defaultMessage: 'Days ago updated',
  },
  durationInState: {
    id: 'comms.automations.wizard.durationInState',
    defaultMessage: 'Duration in this state (Days)',
  },
  carrierName: {id: 'comms.automations.wizard.carrierName', defaultMessage: 'Carrier Name'},
  carrierPlaceholder: {
    id: 'comms.automations.wizard.carrierPlaceholder',
    defaultMessage: 'Enter carrier name...',
  },

  // ── Asistente: paso 2 ──
  step2Title: {
    id: 'comms.automations.wizard.step2Title',
    defaultMessage: 'Through which channel and with what template?',
  },
  sendingChannel: {
    id: 'comms.automations.wizard.sendingChannel',
    defaultMessage: 'Sending channel',
  },
  createTemplate: {
    id: 'comms.automations.wizard.createTemplate',
    defaultMessage: '+ Create Template',
  },
  noTemplatesForChannel: {
    id: 'comms.automations.wizard.noTemplatesForChannel',
    defaultMessage:
      'No templates available for <b>{channel}</b>. Create one in the Templates module and return here.',
  },
  subjectLine: {id: 'comms.automations.wizard.subjectLine', defaultMessage: 'Subject: {subject}'},

  // ── Asistente: paso 3 ──
  step3Title: {
    id: 'comms.automations.wizard.step3Title',
    defaultMessage: 'Final configuration',
  },
  active: {id: 'common.status.active', defaultMessage: 'Active'},
  inactive: {id: 'common.status.inactive', defaultMessage: 'Inactive'},
  scopePersonalOption: {
    id: 'comms.automations.wizard.scopePersonalOption',
    defaultMessage: 'Personal (Assigned to me)',
  },
  scopeOrgOption: {
    id: 'comms.automations.wizard.scopeOrgOption',
    defaultMessage: 'Organization Wide',
  },
  scopeHint: {
    id: 'comms.automations.wizard.scopeHint',
    defaultMessage:
      'Determine if this automation applies to all contacts in the organization or only those assigned to you.',
  },
  description: {id: 'common.field.description', defaultMessage: 'Description'},
  descriptionPlaceholder: {
    id: 'comms.automations.wizard.descriptionPlaceholder',
    defaultMessage: 'Optional description...',
  },
  namePlaceholder: {
    id: 'comms.automations.wizard.namePlaceholder',
    defaultMessage: 'Ex: 30 days expiration reminder',
  },
  delayLabel: {
    id: 'comms.automations.wizard.delayLabel',
    defaultMessage: 'Delay (days after trigger)',
  },
  delayHint: {id: 'comms.automations.wizard.delayHint', defaultMessage: '0 = send immediately'},
  sendTime: {
    id: 'comms.automations.wizard.sendTime',
    defaultMessage: 'Preferred sending time',
  },
  sendTimeHint: {id: 'comms.automations.wizard.sendTimeHint', defaultMessage: 'Hour and minutes'},

  // ── Asistente: resumen y pie ──
  summaryPrefix: {
    id: 'comms.automations.wizard.summaryPrefix',
    defaultMessage: '<b>Summary:</b> Trigger <b>{trigger}</b>',
  },
  summaryRule: {id: 'comms.automations.wizard.summaryRule', defaultMessage: '({rule})'},
  summaryChannel: {
    id: 'comms.automations.wizard.summaryChannel',
    defaultMessage: '→ <b>{channel}</b>',
  },
  summaryTemplate: {
    id: 'comms.automations.wizard.summaryTemplate',
    defaultMessage: '→ Template <b>{template}</b>',
  },
  summaryUnselected: {
    id: 'comms.automations.wizard.summaryUnselected',
    defaultMessage: 'unselected',
  },
  summaryDelay: {
    id: 'comms.automations.wizard.summaryDelay',
    defaultMessage: '· {days, plural, one {# day delay} other {# days delay}}',
  },
  eligibleBirthday: {
    id: 'comms.automations.wizard.eligibleBirthday',
    defaultMessage: 'Contacts with birthday registered:',
  },
  eligibleTodayLabel: {
    id: 'comms.automations.wizard.eligibleTodayLabel',
    defaultMessage: 'Eligible contacts today:',
  },
  noMatchToday: {
    id: 'comms.automations.wizard.noMatchToday',
    defaultMessage: '— no contacts match these conditions today',
  },
  back: {id: 'common.action.back', defaultMessage: 'Back'},
  next: {id: 'common.action.next', defaultMessage: 'Next'},
  templates: {id: 'MENU.item.templates', defaultMessage: 'Templates'},
  createAutomation: {
    id: 'comms.automations.wizard.createAutomation',
    defaultMessage: 'Create automation',
  },

  // ── Asistente: vista previa de plantilla ──
  previewSubject: {
    id: 'comms.automations.wizard.previewSubject',
    defaultMessage: '— {subject}',
  },
  noContent: {
    id: 'comms.automations.wizard.noContent',
    defaultMessage: 'No content available',
  },
  useThisTemplate: {
    id: 'comms.automations.wizard.useThisTemplate',
    defaultMessage: 'Use this template',
  },

  // ── Asistente: toasts ──
  toastTemplateCreated: {
    id: 'comms.automations.toast.templateCreated',
    defaultMessage: 'Template created',
  },
  toastErrorCreateTemplate: {
    id: 'comms.automations.toast.errorCreateTemplate',
    defaultMessage: 'Error creating template',
  },
  toastUnableSaveTemplate: {
    id: 'comms.automations.toast.unableSaveTemplate',
    defaultMessage: 'Unable to save template',
  },
  toastTemplateSavedLibrary: {
    id: 'comms.automations.toast.templateSavedLibrary',
    defaultMessage: 'Template saved to your library',
  },
  toastErrorSaveTemplate: {
    id: 'comms.automations.toast.errorSaveTemplate',
    defaultMessage: 'Error saving template',
  },
  toastTemplateDeleted: {
    id: 'comms.automations.toast.templateDeleted',
    defaultMessage: 'Template deleted',
  },
  toastUnableDeleteTemplate: {
    id: 'comms.automations.toast.unableDeleteTemplate',
    defaultMessage: 'Unable to delete template',
  },
  toastNameRequired: {
    id: 'comms.automations.toast.nameRequired',
    defaultMessage: 'Enter a name for the automation',
  },
  toastUpdated: {id: 'comms.automations.toast.updated', defaultMessage: 'Automation updated'},
  toastCreated: {id: 'comms.automations.toast.created', defaultMessage: 'Automation created'},
  toastErrorSave: {
    id: 'comms.automations.toast.errorSave',
    defaultMessage: 'Error saving automation',
  },
})

/**
 * Categorías de la biblioteca de starters. `ALL` es el filtro; el resto son los
 * valores de `AutomationStarterCategory`.
 */
export const STARTER_CATEGORY_MESSAGES = defineMessages({
  ALL: {id: 'comms.automations.category.all', defaultMessage: 'All'},
  ONBOARDING: {id: 'comms.automations.category.onboarding', defaultMessage: 'Onboarding'},
  LEADS: {id: 'comms.automations.category.leads', defaultMessage: 'Leads'},
  POLICIES: {id: 'comms.automations.category.policies', defaultMessage: 'Policies'},
  RETENTION: {id: 'comms.automations.category.retention', defaultMessage: 'Retention'},
  CROSS_SELL: {id: 'comms.automations.category.crossSell', defaultMessage: 'Cross-sell'},
  BIRTHDAY: {id: 'comms.automations.category.birthday', defaultMessage: 'Birthday'},
})

/**
 * Nombre y descripción de cada starter del sistema, indexados por su `id`.
 * El EN de `automationStarters.ts` sigue siendo el canónico: es el que se
 * persiste al guardar el starter como plantilla propia.
 */
export const STARTER_MESSAGES = defineMessages({
  'system-welcome-new-customer.name': {
    id: 'comms.automations.starter.welcome.name',
    defaultMessage: 'Welcome Your New Customers',
  },
  'system-welcome-new-customer.description': {
    id: 'comms.automations.starter.welcome.description',
    defaultMessage:
      'Send a warm welcome email when a policy is completed. Perfect for onboarding new clients.',
  },
  'system-birthday-wishes.name': {
    id: 'comms.automations.starter.birthday.name',
    defaultMessage: 'Send Customer Birthday Wishes',
  },
  'system-birthday-wishes.description': {
    id: 'comms.automations.starter.birthday.description',
    defaultMessage: 'Automatically send a birthday greeting to your clients on their special day.',
  },
  'system-cross-sell-home.name': {
    id: 'comms.automations.starter.crossSellHome.name',
    defaultMessage: 'Cross-sell: Add Home Insurance',
  },
  'system-cross-sell-home.description': {
    id: 'comms.automations.starter.crossSellHome.description',
    defaultMessage:
      'Reach out to clients with auto coverage who may benefit from adding home insurance.',
  },
  'system-cross-sell-auto.name': {
    id: 'comms.automations.starter.crossSellAuto.name',
    defaultMessage: 'Cross-sell: Add Auto Insurance',
  },
  'system-cross-sell-auto.description': {
    id: 'comms.automations.starter.crossSellAuto.description',
    defaultMessage: "Reach out to clients with home coverage who don't yet have auto insurance.",
  },
  'system-lead-30-days.name': {
    id: 'comms.automations.starter.lead30Days.name',
    defaultMessage: 'Lead 30-Day Follow-up',
  },
  'system-lead-30-days.description': {
    id: 'comms.automations.starter.lead30Days.description',
    defaultMessage:
      'Re-engage leads that have been in your pipeline for 30 days without converting.',
  },
  'system-lead-stage-followup.name': {
    id: 'comms.automations.starter.leadStage.name',
    defaultMessage: 'Lead Stage Progress Update',
  },
  'system-lead-stage-followup.description': {
    id: 'comms.automations.starter.leadStage.description',
    defaultMessage:
      "Keep leads engaged with a status update when they've been in a pipeline stage for a few days.",
  },
  'system-policy-cancellation.name': {
    id: 'comms.automations.starter.policyCancellation.name',
    defaultMessage: 'Policy Cancellation Review',
  },
  'system-policy-cancellation.description': {
    id: 'comms.automations.starter.policyCancellation.description',
    defaultMessage:
      'Alert your team and reach out to clients when a policy status changes — catch cancellations early.',
  },
})

/** Iconos de los disparadores: son parte del diseño, no texto traducible. */
export const ICON_BIRTHDAY = '🎂'
export const ICON_TARGET = '🎯'
export const ICON_SHIELD = '🛡️'
export const ICON_BOLT = '⚡'
export const ICON_PENCIL = '✏️'
export const ICON_ENVELOPE = '✉️'
export const ICON_CHAT = '💬'
export const ICON_MAILBOX = '📨'
export const ICON_GEAR = '⚙️'
export const ICON_HOURGLASS = '⏳'

/** Nombres de canal: no se traducen (guía §17.3). */
export const CHANNEL_EMAIL = 'Email'
export const CHANNEL_SMS = 'SMS'

/**
 * Símbolos sueltos: diseño, no texto (guía §17.3).
 * `SPACE` separa fragmentos del resumen: el extractor recorta los espacios de
 * los extremos del `defaultMessage`, así que no pueden vivir en el mensaje.
 */
export const SPACE = ' '
export const EM_DASH = '—'
export const CHECK_MARK = '✓'
export const CROSS_MARK = '✕'
export const REQUIRED_MARK = '*'

/** Pega un icono a un texto ya traducido sin que el rule marque el template literal. */
export const withIcon = (icon: string, label: string) => `${icon} ${label}`

/** Nombre completo del contacto, con guion largo cuando no hay datos. */
export const fullName = (first?: string, last?: string) =>
  `${first ?? ''} ${last ?? ''}`.trim() || EM_DASH

/** `12 / 30` de la columna "Sent". */
export const sentRatio = (total: number) => `/ ${total}`

/** Sufijos del resumen del disparador en la tabla: `(30d)`, `[Qualified]`. */
export const durationSuffix = (days: number) => ` (${days}d)`
export const bracketSuffix = (value: string) => ` [${value}]`

/** Icono + descriptor de cada disparador (el rule no ve estos object-literals). */
export const TRIGGER_BADGES: Record<string, {icon: string; message: MessageDescriptor}> = {
  HAPPY_BIRTHDAY: {icon: ICON_BIRTHDAY, message: AUTOMATION_MESSAGES.triggerHappyBirthday},
  CONDITIONAL_LEAD: {
    icon: ICON_TARGET,
    message: AUTOMATION_MESSAGES.triggerConditionalLeadShort,
  },
  CONDITIONAL_POLICY: {
    icon: ICON_SHIELD,
    message: AUTOMATION_MESSAGES.triggerConditionalPolicyShort,
  },
}
